import {ColumnDef} from '@tanstack/react-table';
import {AppCheckbox} from 'components/form';
import {AppEllipsisTypography} from 'components/typographies';
import {format} from 'date-fns';
import {extractTextFromHtml} from 'global';
import {useBoardRedux} from 'hooks/board/useBoard';
import {useGetItemDatasByOffsetPagination} from 'hooks/itemStatuses';
import {FieldTypeEnum, ItemDataCreateRequest, ItemDataFieldModel, ItemDataResponse, ItemStatus} from 'models/board';
import {useItemDataState} from 'providers';
import {useCallback, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {sprintSelector} from 'store/sprints';
import TableViewStatusSelect from 'views/board/BoardViews/TableBoardView/components/TableViewStatusSelect';

export function useTableBoardData() {
    const {getAllitemData, getAllitemDataLoading, getAllitemDataResponse} = useGetItemDatasByOffsetPagination();
    const {setItemDatas, itemsDataState} = useItemDataState();

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const {selectedDefaultForm} = useBoardRedux();
    const {selectedSprint} = useSelector(sprintSelector);
    useEffect(() => {
        if (getAllitemDataResponse && !getAllitemDataLoading) {
            setItemDatas(getAllitemDataResponse.items);
        }
    }, [getAllitemDataResponse, getAllitemDataLoading]);

    const getNewlyCreatedItemWithTitleSetToTheFirstField = (
        title: string,
        itemStatusId: string,
    ): ItemDataCreateRequest => {
        const fieldData = selectedDefaultForm?.formFieldsData.map(x => {
            const {...rest} = x;
            if (x.shouldUseAsTheFieldToCreateItem) {
                return {
                    ...rest,
                    value: {...rest.value, value: title},
                };
            }
            return rest;
        });

        const inputData: ItemDataCreateRequest = {
            data: fieldData ?? [],
            itemStatusId: itemStatusId,
            url: window.location.pathname,
            sprintId: selectedSprint?.id !== '' ? selectedSprint?.id : undefined,
            summaryViews: selectedDefaultForm?.summaryViews ?? [],
        };
        return inputData;
    };

    return {
        totalCount: getAllitemDataResponse?.totalCount ?? 0,
        page,
        setPage,
        getNewlyCreatedItemWithTitleSetToTheFirstField,
        rowsPerPage,
        items: itemsDataState ?? [],
        setRowsPerPage,
        getAllitemData,
        getAllitemDataLoading,
    };
}

export const useTableBoardHelper = () => {
    const tableBoardColumns = useCallback(
        (
            itemFields: ItemDataFieldModel[],
            getAllStatusessResponse: ItemStatus[],
            onCellClickHandler: (row: ItemDataResponse) => void,
        ): ColumnDef<ItemDataResponse>[] => {
            return [
                {
                    id: 'id',
                    enableResizing: false,
                    enableHiding: false,
                    header: ({table}) => (
                        <AppCheckbox
                            label=""
                            checked={table.getIsAllRowsSelected()}
                            onChange={table.getToggleAllRowsSelectedHandler()}
                        />
                    ),
                    cell: ({row}) => (
                        <AppCheckbox
                            label=""
                            checked={row.getIsSelected()}
                            disabled={!row.getCanSelect()}
                            onChange={row.getToggleSelectedHandler()}
                        />
                    ),
                    size: 70,
                    enableSorting: false,
                    enableColumnFilter: false,
                },
                {
                    enableHiding: true,
                    header: 'T#',
                    accessorKey: 'ticketNo',
                    size: 100,
                    enableSorting: false,
                    enableColumnFilter: true,
                    meta: {
                        handleCellClick: row => {
                            onCellClickHandler(row.original);
                        },
                        fieldType: FieldTypeEnum.NUMBER,
                    },
                    accessorFn: (row: ItemDataResponse) => {
                        return '#' + row.ticketNo;
                    },
                },
                {
                    enableHiding: true,
                    enableSorting: false,
                    enableColumnFilter: true,
                    header: 'Status',
                    minSize: 200,
                    accessorKey: 'itemStatusId',
                    id: 'itemStatusId',
                    meta: {
                        fieldType: FieldTypeEnum.ItemStatus,
                    },
                    cell: ({row}) => (
                        <TableViewStatusSelect
                            statuses={getAllStatusessResponse ?? []}
                            selectedStatus={row.original.itemStatus ?? null}
                            itemDataId={row.original.id}
                        />
                    ),
                },
                {
                    enableHiding: true,
                    enableSorting: false,
                    enableColumnFilter: false,
                    header: 'Sprint',
                    minSize: 200,
                    accessorFn: (row: ItemDataResponse) => {
                        return row.sprint?.title ?? '';
                    },
                    id: 'sprint',
                    meta: {
                        fieldType: FieldTypeEnum.Text,
                    },
                    cell: ({row, getValue}) => <AppEllipsisTypography text={`${getValue() ?? ''}`} />,
                },
                ...itemFields.map(
                    field =>
                        ({
                            enableHiding: true,
                            header: field.title,
                            meta: {
                                handleCellClick: row => {
                                    onCellClickHandler(row.original);
                                },
                                fieldType: field.fieldType,
                                options: field.defaultValue?.options ?? [],
                            },
                            cell: ({row, getValue}) => <AppEllipsisTypography text={`${getValue() ?? ''}`} />,
                            id: field.uniqueId,
                            enableSorting: true,
                            enableColumnFilter: true,
                            accessorFn: (row: ItemDataResponse) => {
                                const match = row.data.find((f: any) => f.uniqueId === field.uniqueId);
                                if (
                                    field.fieldType === FieldTypeEnum.SelectList ||
                                    field.fieldType === FieldTypeEnum.SelectListWithColor ||
                                    field.fieldType === FieldTypeEnum.AssignTo ||
                                    field.fieldType === FieldTypeEnum.Radio
                                ) {
                                    if (match?.value?.selectedOption?.label) {
                                        return match.value.selectedOption.label;
                                    }
                                } else if (field.fieldType === FieldTypeEnum.NUMBER) {
                                    return match?.value?.integerValue;
                                } else if (field.fieldType === FieldTypeEnum.TextEditor) {
                                    return extractTextFromHtml(match?.value.value ?? '');
                                } else if (field.dataType === 'DATE' || field.dataType === 'DATETIME') {
                                    const val = match?.value.value;
                                    if (val) {
                                        try {
                                            return format(new Date(val), 'PPpp');
                                        } catch {
                                            return val;
                                        }
                                    }
                                }
                                return match?.value.value ?? '';
                            },
                        }) as ColumnDef<ItemDataResponse>,
                ),
            ];
        },
        [],
    );

    return {
        tableBoardColumns,
    };
};
